import React, { useState, useEffect, useMemo, Fragment, ReactNode } from 'react';
import FilterOptions from './components/FilterOptions';
import { vehicleService, CreateVehicleData, UpdateVehicleData, VehicleStatus } from '../../api/vehicles';
import { departamentService, Departament } from '../../api/departament';
import { getApiErrorMessage } from '../../api';
import {
  Car,
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Gauge,
  Wrench,
  CheckCircle2,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  Calendar,
  Fuel,
  Building2,
  Activity,
  AlertCircle,
  Info,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  StatCard,
  Button,
  Badge,
  Input,
  Select,
  Label,
  Modal,
  ConfirmModal,
} from '../../components/ui/SharedLayout';

interface Veiculo {
  id: number;
  placa: string;
  modelo: string;
  marca: string;
  ano: number;
  tipoCombustivel: string;
  orgaoResponsavel: string;
  quilometragemAtual: number;
  status: VehicleStatus;
  department_name: string;
  createdAt: string;
}

const formatVehicleStatus = (status: VehicleStatus) => {
  switch (status) {
    case 'disponivel':
      return 'Disponível';
    case 'manutencao':
      return 'Em manutenção';
    case 'em_uso':
      return 'Em uso';
    default:
      return status;
  }
};

const MAX_LENGTH = {
  modelo: 100,
  marca: 60,
  ano: 4,
  quilometragem: 9999999,
};

interface FormErrors {
  placa?: string;
  modelo?: string;
  marca?: string;
  ano?: string;
  tipoCombustivel?: string;
  quilometragemAtual?: string;
  status?: string;
  departmentName?: string;
}

interface FieldWrapperProps {
  children: ReactNode;
  error?: string;
}

const FieldWrapper = ({ children, error }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {children}
    {error ? (
      <div className="flex items-center gap-1 text-xs text-rose-600">
        <AlertCircle className="h-3 w-3" />
        {error}
      </div>
    ) : null}
  </div>
);

interface CharCounterProps {
  current: number;
  max: number;
}

const CharCounter = ({ current, max }: CharCounterProps) => (
  <div
    className={`text-[11px] font-medium ${
      current > max * 0.9 ? 'text-amber-600' : 'text-slate-400'
    }`}
  >
    {current}/{max}
  </div>
);

const CadastroVeiculos = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<Veiculo | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [filterModalKey, setFilterModalKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [veiculoParaExcluir, setVeiculoParaExcluir] = useState<Veiculo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const ITEMS_PER_PAGE = 20;

  const [formData, setFormData] = useState({
    placa: '',
    modelo: '',
    marca: '',
    ano: '',
    tipoCombustivel: '',
    quilometragemAtual: '',
    departmentName: '',
    status: '',
  });

  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [departments, setDepartments] = useState<Departament[]>([]);

  const filterCategories = {
    Status: [
      { id: 'disponivel', label: 'Disponível' },
      { id: 'manutencao', label: 'Em Manutenção' },
      { id: 'em_uso', label: 'Em Uso' },
    ],
    Combustível: [
      { id: 'Flex', label: 'Flex' },
      { id: 'Gasolina', label: 'Gasolina' },
      { id: 'Diesel', label: 'Diesel' },
    ],
    'Órgão Responsável': [
      { id: 'Secretaria de Obras', label: 'Secretaria de Obras' },
      { id: 'Secretaria de Saúde', label: 'Secretaria de Saúde' },
      { id: 'Secretaria de Educação', label: 'Secretaria de Educação' },
      { id: 'Gabinete do Prefeito', label: 'Gabinete do Prefeito' },
    ],
  };

  const carregarVeiculos = async () => {
    setLoading(true);
    setError(null);
    try {
      const vehicles = await vehicleService.getAllVehicles();
      const veiculosConvertidos = vehicles
        .map((vehicle) => ({
          id: vehicle.id,
          placa: vehicle.license_plate,
          modelo: vehicle.description,
          marca: vehicle.brand,
          ano: vehicle.year,
          tipoCombustivel: vehicle.fuel || 'Flex',
          orgaoResponsavel: vehicle.department_name || '',
          quilometragemAtual: vehicle.mileage || 0,
          status: vehicle.status || 'disponivel',
          department_name: vehicle.department_name || '',
          createdAt: vehicle.created_at || '',
        }))
        .sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
      setVeiculos(veiculosConvertidos);
    } catch (err) {
      console.error('Erro ao carregar veículos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarVeiculos();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await departamentService.getAll();
        setDepartments(data);
      } catch (err) {
        console.error('Erro ao carregar departamentos:', err);
      }
    };
    fetchDepartments();
  }, []);

  const veiculosFiltrados = useMemo(() => {
    let filtered = [...veiculos];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (veiculo) =>
          veiculo.placa.toLowerCase().includes(term) ||
          veiculo.modelo.toLowerCase().includes(term) ||
          veiculo.marca.toLowerCase().includes(term) ||
          veiculo.tipoCombustivel.toLowerCase().includes(term) ||
          veiculo.orgaoResponsavel.toLowerCase().includes(term) ||
          veiculo.status.toLowerCase().includes(term) ||
          veiculo.ano.toString().includes(term) ||
          veiculo.quilometragemAtual.toString().includes(term),
      );
    }

    if (Object.keys(appliedFilters).length > 0) {
      Object.entries(appliedFilters).forEach(([category, values]) => {
        if (values.length > 0) {
          filtered = filtered.filter((veiculo) => {
            if (category === 'Status') {
              return values.includes(veiculo.status);
            } else if (category === 'Combustível') {
              return values.includes(veiculo.tipoCombustivel);
            } else if (category === 'Órgão Responsável') {
              return values.includes(veiculo.orgaoResponsavel);
            }
            return true;
          });
        }
      });
    }

    return filtered;
  }, [searchTerm, appliedFilters, veiculos]);

  const totalPages = Math.max(1, Math.ceil(veiculosFiltrados.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [veiculosFiltrados.length, currentPage, totalPages]);

  const { paginatedVehicles, pageRange } = useMemo(() => {
    const total = veiculosFiltrados.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = veiculosFiltrados.slice(startIndex, endIndex);
    return {
      paginatedVehicles: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [veiculosFiltrados, currentPage, totalPages, ITEMS_PER_PAGE]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const totals = useMemo(() => {
    const total = veiculos.length;
    const disponiveis = veiculos.filter((v) => v.status === 'disponivel').length;
    const emUso = veiculos.filter((v) => v.status === 'em_uso').length;
    const manutencao = veiculos.filter((v) => v.status === 'manutencao').length;
    return { total, disponiveis, emUso, manutencao };
  }, [veiculos]);

  const handleApplyFilter = (selectedFilters: Record<string, string[]>) => {
    setAppliedFilters(selectedFilters);
  };

  const handleOpenFilter = () => {
    setFilterModalKey((k) => k + 1);
    setIsFilterOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.placa.trim()) {
      errors.placa = 'Placa é obrigatória';
    } else if (formData.placa.length !== 7) {
      errors.placa = 'Placa deve ter 7 caracteres';
    } else {
      const placaExistente = veiculos.find(
        (v) =>
          v.placa.toUpperCase() === formData.placa.toUpperCase() &&
          v.id !== veiculoSelecionado?.id,
      );
      if (placaExistente) {
        errors.placa = 'Esta placa já está cadastrada';
      }
    }

    if (!formData.modelo.trim()) {
      errors.modelo = 'Modelo é obrigatório';
    } else if (formData.modelo.length > MAX_LENGTH.modelo) {
      errors.modelo = `Modelo deve ter no máximo ${MAX_LENGTH.modelo} caracteres`;
    }

    if (!formData.marca.trim()) {
      errors.marca = 'Marca é obrigatória';
    } else if (formData.marca.length > MAX_LENGTH.marca) {
      errors.marca = `Marca deve ter no máximo ${MAX_LENGTH.marca} caracteres`;
    }

    const currentYear = new Date().getFullYear();
    const anoNum = Number(formData.ano);
    if (!formData.ano) {
      errors.ano = 'Ano é obrigatório';
    } else if (isNaN(anoNum) || anoNum < 1900 || anoNum > currentYear + 1) {
      errors.ano = `Ano deve estar entre 1900 e ${currentYear + 1}`;
    }

    if (!formData.tipoCombustivel) {
      errors.tipoCombustivel = 'Selecione o tipo de combustível';
    }

    if (formData.quilometragemAtual !== '') {
      const kmNum = Number(formData.quilometragemAtual);
      if (isNaN(kmNum) || kmNum < 0 || kmNum > MAX_LENGTH.quilometragem) {
        errors.quilometragemAtual = `Quilometragem deve estar entre 0 e ${MAX_LENGTH.quilometragem.toLocaleString('pt-BR')}`;
      }
    }

    if (!formData.status) {
      errors.status = 'Selecione o status do veículo';
    }

    if (!formData.departmentName) {
      errors.departmentName = 'Selecione o órgão / departamento';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    setVeiculoSelecionado(null);
    setFormData({
      placa: '',
      modelo: '',
      marca: '',
      ano: '',
      tipoCombustivel: '',
      quilometragemAtual: '',
      departmentName: '',
      status: '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (veiculo: Veiculo) => {
    setVeiculoSelecionado(veiculo);
    setFormData({
      placa: veiculo.placa || '',
      modelo: veiculo.modelo || '',
      marca: veiculo.marca || '',
      ano: veiculo.ano ? String(veiculo.ano) : '',
      tipoCombustivel: veiculo.tipoCombustivel || '',
      quilometragemAtual: veiculo.quilometragemAtual ? String(veiculo.quilometragemAtual) : '',
      departmentName: veiculo.department_name || veiculo.orgaoResponsavel || '',
      status: veiculo.status || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'placa') {
      const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
      setFormData((prev) => ({ ...prev, placa: formatted }));
      if (formErrors.placa) {
        setFormErrors((prev) => ({ ...prev, placa: undefined }));
      }
      return;
    }

    if (name === 'modelo') {
      const truncated = value.slice(0, MAX_LENGTH.modelo);
      setFormData((prev) => ({ ...prev, modelo: truncated }));
      if (formErrors.modelo) {
        setFormErrors((prev) => ({ ...prev, modelo: undefined }));
      }
      return;
    }

    if (name === 'marca') {
      const truncated = value.slice(0, MAX_LENGTH.marca);
      setFormData((prev) => ({ ...prev, marca: truncated }));
      if (formErrors.marca) {
        setFormErrors((prev) => ({ ...prev, marca: undefined }));
      }
      return;
    }

    if (name === 'ano') {
      const formatted = value.replace(/[^0-9]/g, '').slice(0, MAX_LENGTH.ano);
      setFormData((prev) => ({ ...prev, ano: formatted }));
      if (formErrors.ano) {
        setFormErrors((prev) => ({ ...prev, ano: undefined }));
      }
      return;
    }

    if (name === 'quilometragemAtual') {
      const formatted = value.replace(/[^0-9]/g, '');
      const numValue = Number(formatted);
      const truncated =
        formatted === '' ? '' : String(Math.min(numValue, MAX_LENGTH.quilometragem));
      setFormData((prev) => ({
        ...prev,
        quilometragemAtual: truncated,
      }));
      if (formErrors.quilometragemAtual) {
        setFormErrors((prev) => ({ ...prev, quilometragemAtual: undefined }));
      }
      return;
    }

    if (name === 'tipoCombustivel' && formErrors.tipoCombustivel) {
      setFormErrors((prev) => ({ ...prev, tipoCombustivel: undefined }));
    }
    if (name === 'status' && formErrors.status) {
      setFormErrors((prev) => ({ ...prev, status: undefined }));
    }
    if (name === 'departmentName' && formErrors.departmentName) {
      setFormErrors((prev) => ({ ...prev, departmentName: undefined }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!veiculoSelecionado) {
        const novoVeiculoData: CreateVehicleData = {
          brand: formData.marca.trim(),
          year: Number(formData.ano),
          license_plate: formData.placa.toUpperCase(),
          description: formData.modelo.trim(),
          department_name: formData.departmentName,
          mileage: formData.quilometragemAtual ? Number(formData.quilometragemAtual) : 0,
          employee_id: null,
          status: formData.status as VehicleStatus,
          fuel: formData.tipoCombustivel || 'Flex',
        };
        await vehicleService.createVehicle(novoVeiculoData);
        await carregarVeiculos();
        setIsModalOpen(false);
      } else {
        const veiculoEditadoData: UpdateVehicleData = {
          brand: formData.marca.trim(),
          year: Number(formData.ano),
          license_plate: formData.placa.toUpperCase(),
          description: formData.modelo.trim(),
          department_name: formData.departmentName,
          mileage: formData.quilometragemAtual ? Number(formData.quilometragemAtual) : 0,
          employee_id: null,
          status: formData.status as VehicleStatus,
          fuel: formData.tipoCombustivel || 'Flex',
        };

        await vehicleService.updateVehicle(veiculoSelecionado.id, veiculoEditadoData);
        await carregarVeiculos();
        setIsModalOpen(false);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar veículo'));
      console.error('Erro ao salvar veículo:', err);
    } finally {
      setLoading(false);
    }
  };

  const abrirConfirmacaoExclusao = (veiculo: Veiculo) => {
    setVeiculoParaExcluir(veiculo);
    setIsDeleteModalOpen(true);
  };

  const confirmarExclusaoVeiculo = async () => {
    if (!veiculoParaExcluir) return;

    setDeleteLoading(true);
    setError(null);

    try {
      await vehicleService.deleteVehicle(veiculoParaExcluir.id);
      await carregarVeiculos();
      setIsDeleteModalOpen(false);
      setVeiculoParaExcluir(null);
    } catch (err) {
      const errorMessage = 'Erro ao excluir veículo';
      setError(errorMessage);
      console.error('Erro ao excluir veículo:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelarExclusaoVeiculo = () => {
    if (deleteLoading) return;
    setIsDeleteModalOpen(false);
    setVeiculoParaExcluir(null);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Cadastro de Veículos" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Gestão de veículos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Cadastre e acompanhe a frota
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                de forma prática e organizada
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button variant="secondary" onClick={handleOpenFilter} className="h-10 px-4">
              <Filter className="h-4 w-4 stroke-[1.8]" />
              Filtros
            </Button>
            <Button variant="primary" onClick={handleCreate} className="h-10 px-4 shadow-[0_4px_14px_-4px_rgba(2,132,199,0.4)]">
              <Plus className="h-[18px] w-[18px] stroke-[2.2]" />
              Novo veículo
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-7">
          <StatCard
            icon={<Car className="h-5 w-5 stroke-[1.8]" />}
            label="Total de Veículos"
            value={totals.total}
            trend={<span>Cadastrados no sistema</span>}
            variant="deep"
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5 stroke-[1.8]" />}
            label="Disponíveis"
            value={totals.disponiveis}
            trend={<span>Prontos para uso</span>}
            variant="primary"
          />
          <StatCard
            icon={<Clock className="h-5 w-5 stroke-[1.8]" />}
            label="Em Uso"
            value={totals.emUso}
            trend={<span>Em viagem ou operação</span>}
            variant="sky"
          />
          <StatCard
            icon={<Wrench className="h-5 w-5 stroke-[1.8]" />}
            label="Em Manutenção"
            value={totals.manutencao}
            trend={<span>Oficina / reparos</span>}
            variant="rich"
          />
        </div>

        <SectionCard>
          <CardHeader>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                Veículos
              </h2>
              <p className="text-[13px] text-slate-500 mt-0.5">
                {loading
                  ? 'Carregando registros...'
                  : pageRange.total === 0
                    ? 'Nenhum veículo encontrado'
                    : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} veículos listados (total cadastrado: ${veiculos.length})`}
              </p>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-vehicles"
                placeholder="Pesquisar placa, modelo, marca, órgão..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {error && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando veículos...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Marca</Th>
                      <Th>Ano</Th>
                      <Th>Combustível</Th>
                      <Th>Quilometragem</Th>
                      <Th>Órgão responsável</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum veículo encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedVehicles.map((veiculo) => (
                        <Tr key={veiculo.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-900">
                                  {veiculo.modelo}
                                </div>
                                <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500">
                                  {veiculo.placa}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className="font-medium text-slate-700">
                              {veiculo.marca}
                            </span>
                          </Td>
                          <Td>
                            <span className="font-medium text-slate-700">
                              {veiculo.ano}
                            </span>
                          </Td>
                          <Td>
                            <Badge tone="blue" className="!text-[12px] !px-2.5 !py-1">
                              {veiculo.tipoCombustivel}
                            </Badge>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-slate-700">
                              <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-semibold tabular-nums">
                                {veiculo.quilometragemAtual.toLocaleString('pt-BR')}
                              </span>
                              <span className="text-slate-400 text-[12px]">km</span>
                            </div>
                          </Td>
                          <Td>
                            <span className="font-medium text-slate-700">
                              {veiculo.orgaoResponsavel || '-'}
                            </span>
                          </Td>
                          <Td>
                            {veiculo.status === 'disponivel' ? (
                              <Badge tone="emerald">
                                {formatVehicleStatus(veiculo.status)}
                              </Badge>
                            ) : veiculo.status === 'manutencao' ? (
                              <Badge tone="amber">
                                {formatVehicleStatus(veiculo.status)}
                              </Badge>
                            ) : (
                              <Badge tone="blue">
                                {formatVehicleStatus(veiculo.status)}
                              </Badge>
                            )}
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleEdit(veiculo)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                                title="Editar veículo"
                                aria-label="Editar veículo"
                              >
                                <Edit2 className="h-[15px] w-[15px] stroke-[1.8]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => abrirConfirmacaoExclusao(veiculo)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                                title="Excluir veículo"
                                aria-label="Excluir veículo"
                              >
                                <Trash2 className="h-[15px] w-[15px] stroke-[1.8]" />
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {isFilterOpen && (
          <FilterOptions
            key={filterModalKey}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilter={handleApplyFilter}
            filterCategories={filterCategories}
            initialFilters={appliedFilters}
          />
        )}

        {isModalOpen && (
          <Modal
            onClose={() => !loading && setIsModalOpen(false)}
            maxWidthClass="max-w-2xl"
            footer={
              <Fragment>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    const form = document.getElementById('vehicle-form') as HTMLFormElement;
                    if (form) form.requestSubmit();
                  }}
                >
                  {loading ? (
                    <Fragment>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Salvando...
                    </Fragment>
                  ) : (
                    <Fragment>
                      <CheckCircle2 className="h-4 w-4 stroke-[1.8]" />
                      Salvar veículo
                    </Fragment>
                  )}
                </Button>
              </Fragment>
            }
          >
            <div className="flex items-start justify-between gap-4 pb-5 border-b border-slate-100 mb-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.55)]">
                  <Car className="h-[20px] w-[20px] stroke-[1.9]" />
                </div>
                <div>
                  <h3 className="text-[19px] font-bold tracking-tight text-slate-900">
                    {veiculoSelecionado ? 'Editar veículo' : 'Novo veículo'}
                  </h3>
                  <p className="mt-1 text-[13px] text-slate-500">
                    {veiculoSelecionado
                      ? 'Atualize os dados cadastrais e de status do veículo.'
                      : 'Preencha as informações abaixo para cadastrar um novo veículo na frota.'}
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                <AlertCircle className="h-5 w-5 shrink-0 text-rose-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-rose-800">Erro ao salvar</p>
                  <p className="text-sm text-rose-700 mt-0.5">{error}</p>
                </div>
              </div>
            )}

            <form
              id="vehicle-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-6"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Dados de identificação
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldWrapper error={formErrors.placa}>
                    <Label htmlFor="modal-placa">
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5 text-slate-500" />
                        Placa <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <Input
                      id="modal-placa"
                      name="placa"
                      type="text"
                      value={formData.placa}
                      onChange={handleInputChange}
                      required
                      placeholder="ABC1D23"
                      maxLength={7}
                      className={`font-mono tracking-wide uppercase ${formErrors.placa ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                    />
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.ano}>
                    <Label htmlFor="modal-ano">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-slate-500" />
                        Ano <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <div className="relative">
                      <Input
                        id="modal-ano"
                        name="ano"
                        type="text"
                        value={formData.ano}
                        onChange={handleInputChange}
                        required
                        placeholder="2024"
                        maxLength={MAX_LENGTH.ano}
                        className={`${formErrors.ano ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <CharCounter current={formData.ano.length} max={MAX_LENGTH.ano} />
                      </div>
                    </div>
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.marca}>
                    <Label htmlFor="modal-marca">
                      <div className="flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5 text-slate-500" />
                        Marca <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <div className="relative">
                      <Input
                        id="modal-marca"
                        name="marca"
                        type="text"
                        value={formData.marca}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex: Toyota, Volkswagen, Fiat"
                        maxLength={MAX_LENGTH.marca}
                        className={`${formErrors.marca ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <CharCounter current={formData.marca.length} max={MAX_LENGTH.marca} />
                      </div>
                    </div>
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.modelo}>
                    <Label htmlFor="modal-modelo">
                      <div className="flex items-center gap-1.5">
                        <Car className="h-3.5 w-3.5 text-slate-500" />
                        Modelo <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <div className="relative">
                      <Input
                        id="modal-modelo"
                        name="modelo"
                        type="text"
                        value={formData.modelo}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex: Corolla Cross, Gol, Palio"
                        maxLength={MAX_LENGTH.modelo}
                        className={`${formErrors.modelo ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <CharCounter current={formData.modelo.length} max={MAX_LENGTH.modelo} />
                      </div>
                    </div>
                  </FieldWrapper>
                </div>
              </div>

              <div className="h-px bg-slate-100" />

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Especificações e uso
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldWrapper error={formErrors.tipoCombustivel}>
                    <Label htmlFor="modal-tipoCombustivel">
                      <div className="flex items-center gap-1.5">
                        <Fuel className="h-3.5 w-3.5 text-slate-500" />
                        Tipo de Combustível <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <Select
                      id="modal-tipoCombustivel"
                      name="tipoCombustivel"
                      value={formData.tipoCombustivel}
                      onChange={handleInputChange}
                      required
                      className={`${formErrors.tipoCombustivel ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                    >
                      <option value="">Selecione...</option>
                      <option value="Flex">Flex</option>
                      <option value="Gasolina">Gasolina</option>
                      <option value="Diesel">Diesel</option>
                    </Select>
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.quilometragemAtual}>
                    <Label htmlFor="modal-quilometragem">
                      <div className="flex items-center gap-1.5">
                        <Gauge className="h-3.5 w-3.5 text-slate-500" />
                        Quilometragem Atual
                      </div>
                    </Label>
                    <div className="relative">
                      <Input
                        id="modal-quilometragem"
                        name="quilometragemAtual"
                        type="text"
                        value={formData.quilometragemAtual}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        className={`pr-10 tabular-nums ${formErrors.quilometragemAtual ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
                        km
                      </span>
                    </div>
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.departmentName}>
                    <Label htmlFor="modal-departmentName">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-slate-500" />
                        Órgão / Departamento <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <Select
                      id="modal-departmentName"
                      name="departmentName"
                      value={formData.departmentName}
                      onChange={handleInputChange}
                      required
                      className={`${formErrors.departmentName ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                    >
                      <option value="">Selecione...</option>
                      {departments.map((dep) => (
                        <option key={dep.id} value={dep.name}>
                          {dep.name}
                        </option>
                      ))}
                    </Select>
                  </FieldWrapper>

                  <FieldWrapper error={formErrors.status}>
                    <Label htmlFor="modal-status">
                      <div className="flex items-center gap-1.5">
                        <Activity className="h-3.5 w-3.5 text-slate-500" />
                        Status <span className="text-rose-500">*</span>
                      </div>
                    </Label>
                    <Select
                      id="modal-status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      disabled={
                        !!veiculoSelecionado && veiculoSelecionado.status === 'manutencao'
                      }
                      className={`${
                        !!veiculoSelecionado && veiculoSelecionado.status === 'manutencao'
                          ? '!bg-slate-50 !text-slate-500 !cursor-not-allowed'
                          : ''
                      } ${formErrors.status ? '!border-rose-400 focus:!ring-rose-500/20 focus:!border-rose-500' : ''}`}
                    >
                      <option value="">Selecione...</option>
                      <option value="disponivel">Disponível</option>
                      <option value="manutencao">Em Manutenção</option>
                      <option value="em_uso">Em Uso</option>
                    </Select>
                    {!!veiculoSelecionado && veiculoSelecionado.status === 'manutencao' && (
                      <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3 w-3" />
                        Status travado: conclua a manutenção primeiro
                      </p>
                    )}
                  </FieldWrapper>
                </div>
              </div>
            </form>
          </Modal>
        )}

        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title={
            veiculoParaExcluir
              ? `Excluir veículo ${veiculoParaExcluir.placa}?`
              : 'Excluir veículo?'
          }
          description={
            veiculoParaExcluir ? (
              <span>
                Você está prestes a remover{' '}
                <strong className="font-semibold text-slate-700">
                  {veiculoParaExcluir.marca} {veiculoParaExcluir.modelo}
                </strong>{' '}
                (placa{' '}
                <span className="font-mono font-semibold text-slate-700">
                  {veiculoParaExcluir.placa}
                </span>
                ). Esta ação é irreversível e não poderá ser desfeita.
              </span>
            ) : (
              'Esta ação é irreversível e não poderá ser desfeita.'
            )
          }
          confirmLabel={
            deleteLoading ? (
              <span className="inline-flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                Excluindo...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Trash2 className="h-4 w-4 stroke-[1.8]" />
                Sim, excluir veículo
              </span>
            )
          }
          cancelLabel="Cancelar"
          confirmVariant="danger"
          onConfirm={confirmarExclusaoVeiculo}
          onCancel={cancelarExclusaoVeiculo}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default CadastroVeiculos;
